'use client';

import React, { type FC } from 'react';
import { Credits } from './Credits';
import { FaSteam } from './icons';
import { isCsShotUzHost } from '../runtime/host';
import { useHost } from '../runtime/RuntimeContext';
import { formatUsdPrice } from '../utils/formatters';
import { cn } from '../utils/cn';

/** Whether this domain may show reference Steam prices at all — the Uzbek one may not.
 *  That rule lives here instead of at the call sites because it used to be copied by hand
 *  into every screen with a price, so every new screen was a fresh chance to forget it;
 *  six of them had, and showed dollars on `cs-shot.uz`.
 *
 *  `ItemPrice` asks this itself, so passing `steamPrice` straight through is safe: forget
 *  everything and the price still hides. This hook is for call sites that wrap the price in
 *  a container of their own — an `OverlayBadge`, a `Cell` — and need the answer before they
 *  render the container, otherwise an empty pill or an empty table row stays on screen.
 *  In a list, call it once above the `map` and pair it with the row's own `!= null` check;
 *  hooks can't be called per row. */
export function useSteamPriceAllowed(): boolean {
  return !isCsShotUzHost(useHost());
}

/** The container-level condition for a single item: allowed by the host and present. */
export function useShowSteamPrice(steamPrice: number | null | undefined): boolean {
  const allowed = useSteamPriceAllowed();
  return allowed && steamPrice != null;
}

interface ItemPriceProps {
  /** Price in credits — the primary number. */
  credits?: number | null;
  /** Reference USD price from Steam (`item.steamPrice` / `prize.steamPriceUsd`). Hidden on
   *  the Uzbek domain and when absent, so it can be passed straight through. */
  steamPrice?: number | null;
  creditsIconSize?: number | string;
  steamIconSize?: number | string;
  /** Off for rows that already carry the Steam glyph elsewhere — a `Cell` with
   *  `before={<FaSteam />}`, where a second icon in `after` would double it up. */
  showSteamIcon?: boolean;
  /** Tight columns: `k`/`M` for the credits (as `Credits compact` does) and whole dollars,
   *  no cents, for the Steam price. */
  compact?: boolean;
  className?: string;
}

/** The price of an item: credits, then the reference Steam price after a `·` separator —
 *  icon and dollar amount, no parentheses. The separator exists because the coins icon and
 *  the Steam icon land right next to each other otherwise and read as one glued symbol;
 *  it only appears when both halves are present, so a Steam price standing alone in its own
 *  badge doesn't start with a stray dot. The dot sits at 60% opacity: measured against the
 *  darkest and brightest real backgrounds among the places this renders, that's the lowest
 *  opacity clearing 3:1 contrast everywhere it can (one background is bright enough that
 *  even full-strength text falls short there).
 *
 *  Takes no font, weight or color prop on purpose — this is one price line, not a caption
 *  glued to another number, so it inherits size, weight and color from whichever row it's
 *  placed in. Give the row's own wrapper the font, not this component. `className` is for
 *  that row to nudge layout (a `gap-1.5` instead of the default `gap-1`), and it merges,
 *  so passing typography there works but means the row lost its own.
 *
 *  Both numbers are formatted by one pair of helpers (`formatCreditsNumber`,
 *  `formatUsdPrice`) that share a grouping separator, so `2 500 000 🪙 · 🎮 $10 000.00`
 *  breaks its thousands the same way on both sides of the dot. */
export const ItemPrice: FC<ItemPriceProps> = ({
  credits,
  steamPrice,
  creditsIconSize,
  steamIconSize = 12,
  showSteamIcon = true,
  compact = false,
  className,
}) => {
  const showSteamPrice = useShowSteamPrice(steamPrice);

  if (credits == null && !showSteamPrice) return null;

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {credits != null && <Credits amount={credits} iconSize={creditsIconSize} compact={compact} />}
      {showSteamPrice && (
        <span className="flex items-center">
          {credits != null && <span aria-hidden className="me-1.5 opacity-60">·</span>}
          {showSteamIcon && <FaSteam size={steamIconSize} className="me-1 shrink-0" />}
          ${formatUsdPrice(steamPrice!, { whole: compact })}
        </span>
      )}
    </span>
  );
};
